import React, {
  useCallback,
  useEffect,
  useState,
  useRef,
  useMemo,
} from "react";
import {
  Button,
  DevNote,
  FormFieldSet,
  PageTitle,
  showToast,
} from "../../../../components";
import PageHeader from "../../../../components/Common/PageHeader";
import useFormHandler from "../../../../components/Validations/useFormHandler";
import {
  clientCategoryOptions,
  jobTerminationNoticeOptions,
  netTermsOptions,
} from "../../../../assets/data/projectData";
import { CommissionType, CurrencyType } from "../../../../assets/data/enumData";
import { useDispatch, useSelector } from "react-redux";
import {
  addToDraft,
  getClientOneStart,
  updateClientStart,
} from "../../../../redux/slices/client/clientSlice";
import { useLoginUser } from "../../../../hooks/useLoginUser";
import useDropdownData from "../../../../hooks/useDropdownData";
import Locations from "../../ComponentsList/Locations";
import { debounce } from "../../../../utils";

const GeneralInformation = ({
  setActiveTab,
  isEditable,
  editClient = "",
  clientId,
}) => {
  const dispatch = useDispatch();
  const clientProfileUiid = useSelector(
    (state) => state.client.clientProfileUiid
  );
  const clientDetails = useSelector(
    (state) => state.client.currentClient.clientDetails
  );
  const [locations, setLocations] = useState([0]);
  const [removedLocationIds, setRemovedLocationIds] = useState([]);

  const [placementType, setPlacementType] = useState(1);
  const containerRef = useRef(null);
  const { userData: loginUserData } = useLoginUser();
  const loginUserUiid = loginUserData?.uiid;

  const initialState = {
    currency: "",
    //reccurring placement
    netTerms: "",
    clientCategory: undefined,
    jobTerminationNotice: "15",
    //permanent placement
    placementNetTerms: undefined,
    commission: undefined,
    commissionType: undefined,
    address1: undefined,
    address2: undefined,
    country: undefined,
    state: undefined,
    city: undefined,
    zipCode: undefined,
    // markClientLocation: undefined,
    locations: [
      {
        _id: 1,
        address1: "",
        address2: "",
        country: "",
        state: "",
        city: "",
        zipCode: "",
        isDefaultInvoice: false,
      },
    ],
  };

  const { formData, errors, handleChange, validateAllFields, setFormData } =
    useFormHandler(initialState, {
      // currency: { type: "text", required: true },
      currency: { type: "select", required: true, number: true },
    });
  console.log("formData",formData);
  const handlePlacementType = (value) => {
    setPlacementType(value);

    const resetFields =
      value === 1
        ? {
            placementNetTerms: "",
            commission: "",
            commissionType: "",
            netTerms: formData.netTerms?.value ?? formData.netTerms,
            clientCategory:
              (formData.clientCategory?.value ?? formData.clientCategory) || "",
            jobTerminationNotice:
              (formData.jobTerminationNotice?.value ??
                formData.jobTerminationNotice) ||
              "15",
          }
        : {
            netTerms: "",
            clientCategory: "",
            jobTerminationNotice: "",
            placementNetTerms: formData.placementNetTerms,
            commission: formData.commission,
            commissionType: formData.commissionType,
          };

    Object.entries(resetFields).forEach(([key, val]) => {
      handleChange(key, val);
    });
  };
  // to get data for prefil
  useEffect(() => {
    if (clientProfileUiid) {
      dispatch(
        getClientOneStart({
          clientUiid: clientProfileUiid,
          retrieve: `generalInfo`,
        })
      );
    }
    // }, [clientProfileUiid]);
  }, [clientId]);

  const { data: countryOptions, handleScroll: handleCountriesScroll } =
    useDropdownData("countriesUsAndCanada");

  const countryId = countryOptions.find(
    (item) => item.label === formData.country
  )?.id;

  const {
    data: stateOptions,
    searchValue: stateSearch,
    handleSearch: handleStateSearch,
    resetSearch: resetStateSearch,
    handleScroll: handleStateScroll,
  } = useDropdownData(
    "states",
    10,
    { country: formData.country },
    { countrySearch: countryId }
  );

  const {
    data: cityOptions,
    searchValue: citySearch,
    handleSearch: handleCitySearch,
    resetSearch: resetCitySearch,
    handleScroll: handleCityScroll,
  } = useDropdownData(
    "cities",
    10,
    { country: formData.country, state: formData.state },
    { countrySearch: countryId, stateSearch: formData.state }
  );
  const handleSave = () => {
    const isValid = validateAllFields();
    if (!isValid) {
      console.log("Please fix the errors before submitting.");
      return;
    }
    // if (!clientProfileUiid) {
    //   showToast("Client's Business Information Not Saved", "error");
    //   return;
    // }

    const locationsPayload = {};
    const safeLocations = Array.isArray(formData.locations)
      ? formData.locations
      : [];

    safeLocations.forEach((loc, index) => {
      if (
        loc?.address1 ||
        loc?.address2 ||
        loc?.city ||
        loc?.state ||
        loc?.zipCode ||
        loc?.country
      ) {
        locationsPayload[`generalInfo.locations.${index}.isDefaultInvoice`] =
          loc.isDefaultInvoice || false;
        locationsPayload[`generalInfo.locations.${index}.address1`] =
          loc.address1 || "";
        locationsPayload[`generalInfo.locations.${index}.address2`] =
          loc.address2 || "";
        locationsPayload[`generalInfo.locations.${index}.city`] =
          loc.city || "";
        locationsPayload[`generalInfo.locations.${index}.state`] =
          loc.state || "";
        locationsPayload[`generalInfo.locations.${index}.zipCode`] =
          loc.zipCode && loc.zipCode.toString().length <= 15
            ? Number(loc.zipCode)
            : null;
        locationsPayload[`generalInfo.locations.${index}.country`] =
          loc.country || "";
      }
    });

    const payloadValue = {
      generalInfo: {
        placementDetails: {
          recurring: {
            netTerms: (formData?.netTerms?.value ?? formData.netTerms) || "",
            category:
              (formData?.clientCategory?.value ?? formData?.clientCategory) ||
              "",
            jobTerminationNotice:
              (formData?.jobTerminationNotice?.value ??
                formData?.jobTerminationNotice) ||
              "",
          },
          permanent: {
            netTerms: formData?.placementNetTerms || "",
            commission: formData?.commission || "",
            commissionType: formData?.commissionType || "",
          },
        },
        currencyType: formData?.currency || "",
        invoiceLocation: {
          address1: formData?.address1 || "",
          address2: formData?.address2 || "",
          city: formData?.city || "",
          state: formData?.state || "",
          zipCode: formData?.zipCode ? Number(formData?.zipCode) : "",
          country: formData?.country || "",
        },
      },
    };

    let prepareSubmissionData;
    //working one
    // const clientHasNoLocations =
    //   !clientDetails?.generalInfo ||
    //   !Array.isArray(clientDetails.generalInfo.locations) ||
    //   clientDetails.generalInfo.locations.length === 0;

    // if (clientHasNoLocations) {
    //   prepareSubmissionData = {
    //     coll: "Client",
    //     uiid: clientProfileUiid,
    //     payload: [
    //       {
    //         type: "SET",
    //         value: payloadValue,
    //       },
    //       {
    //         type: "PUSH",
    //         value: {
    //           "generalInfo.locations": safeLocations
    //             .filter(
    //               ({
    //                 country,
    //                 city,
    //                 state,
    //                 zipCode,
    //                 address1,
    //                 address2,
    //                 isDefaultInvoice,
    //               }) =>
    //                 country ||
    //                 city ||
    //                 state ||
    //                 zipCode ||
    //                 address1 ||
    //                 address2 ||
    //                 isDefaultInvoice
    //             )
    //             .map(
    //               ({
    //                 address1,
    //                 address2,
    //                 city,
    //                 state,
    //                 zipCode,
    //                 country,
    //                 isDefaultInvoice,
    //               }) => ({
    //                 address1: address1 || undefined,
    //                 address2: address2 || undefined,
    //                 city: city || undefined,
    //                 state: state || undefined,
    //                 zipCode: zipCode ? Number(zipCode) : undefined,
    //                 country: country || undefined,
    //                 isDefaultInvoice: isDefaultInvoice || false,
    //               })
    //             ),
    //         },
    //       },
    //     ],
    //   };
    // } else {
    //   prepareSubmissionData = {
    //     coll: "Client",
    //     uiid: clientProfileUiid,
    //     payload: [
    //       {
    //         type: "SET",
    //         value: {
    //           ...payloadValue,
    //           ...locationsPayload,
    //         },
    //       },
    //     ],
    //   };
    // }

    //testing one
    const isValidObjectId = (id) =>
      typeof id === "string" &&
      /^[a-f\d]{24}$/i.test(id) &&
      !/^\d+$/.test(id);
    console.log("safeLocations",safeLocations);
    const existingLocations = safeLocations.filter((loc) =>
      isValidObjectId(loc._id)
    );
    const newLocations = safeLocations.map(
      (loc) =>loc?._id === 1 ? loc : undefined
    );
    console.log("existingLocations",existingLocations);
    console.log("newLocations",newLocations);
    // return;
    const hasNoLocations =
      !clientDetails?.generalInfo ||
      !Array.isArray(clientDetails.generalInfo.locations) ||
      clientDetails.generalInfo.locations.length === 0;

    const locationSetPayload = existingLocations.map(
      ({
        _id,
        address1,
        address2,
        city,
        state,
        zipCode,
        country,
        isDefaultInvoice,
      }) => ({
        [`generalInfo.locations.${_id}`]: {
          address1: address1 || undefined,
          address2: address2 || undefined,
          city: city || undefined,
          state: state || undefined,
          zipCode: zipCode ? Number(zipCode) : undefined,
          country: country || undefined,
          isDefaultInvoice: isDefaultInvoice || false,
        },
      })
    );

    const locationPushPayload = newLocations?.filter(Boolean)?.map(
      ({
        address1,
        address2,
        city,
        state,
        zipCode,
        country,
        isDefaultInvoice,
      }) => ({
        address1: address1 || undefined,
        address2: address2 || undefined,
        city: city || undefined,
        state: state || undefined,
        zipCode: zipCode ? Number(zipCode) : undefined,
        country: country || undefined,
        isDefaultInvoice: isDefaultInvoice || false,
      })
    ) || [];

    const locationPullPayload = removedLocationIds
      ?.filter(isValidObjectId)
      .map((id) => ({
        "generalInfo.locations": { _id: id },
      }));

    const pullPayloadArray = [];
    const setPushPayloadArray = [];

    if (locationPullPayload?.length) {
      pullPayloadArray.push({
        type: "PULL",
        value: locationPullPayload,
      });
    }

    if (hasNoLocations && locationPushPayload.length) {
      setPushPayloadArray.push({
        type: "SET",
        value: { generalInfo: payloadValue?.generalInfo || {} },
      });
      setPushPayloadArray.push({
        type: "PUSH",
        value: { "generalInfo.locations": locationPushPayload },
      });
    } else {
      if (Object.keys(payloadValue || {}).length) {
        setPushPayloadArray.push({
          type: "SET",
          value: {
            ...payloadValue,
            // ...locationsPayload,
            ...Object.assign({}, ...locationSetPayload),
          },
        });
      }

      if (locationPushPayload.length) {
        // setPushPayloadArray.push({
        //   type: "SET",
        //   value: { "generalInfo": payloadValue?.generalInfo || {} },
        // });
        setPushPayloadArray.push({
          type: "PUSH",
          value: { "generalInfo.locations": locationPushPayload },
        });
      }
    }

    if (locationPullPayload?.length) {
      let completedPulls = 0;

      locationPullPayload.forEach((pullItem) => {
        dispatch(
          updateClientStart({
            coll: "Client",
            uiid: clientProfileUiid,
            payload: [{ type: "PULL", value: pullItem }],
            onSuccess: () => {
              completedPulls++;
              if (completedPulls === locationPullPayload.length) {
                if (setPushPayloadArray.length) {
                  dispatch(
                    updateClientStart({
                      coll: "Client",
                      uiid: clientProfileUiid,
                      payload: setPushPayloadArray,
                      onSuccess: () => {
                        dispatch(
                          getClientOneStart({
                            clientUiid: clientProfileUiid,
                            retrieve: "generalInfo",
                          })
                        );
                        setActiveTab((prev) => prev + 1);
                      },
                    })
                  );
                }
              }
            },
          })
        );
      });
    } else {
      dispatch(
        updateClientStart({
          coll: "Client",
          uiid: clientProfileUiid,
          payload: setPushPayloadArray,
          onSuccess: () => {
            dispatch(
              getClientOneStart({
                clientUiid: clientProfileUiid,
                retrieve: "generalInfo",
              })
            );
            setActiveTab((prev) => prev + 1);
          },
        })
      );
    }
  };

  const handleBack = () => {
    setTimeout(() => {
      // Move to back tab
      setActiveTab((prev) => prev - 1);
    }, 500);
  };
  //shiva
  // const addMoreLocations = () => {
  //   // const updatedLocations = [...locations, locations.length];
  //   // setLocations(updatedLocations);

  //   // const updatedFormData = { ...formData };
  //   const updatedFormData = {
  //     ...formData,
  //     locations: formData.locations ? [...formData.locations.map(loc => ({ ...loc }))] : [],
  //   };

  //   if (!updatedFormData.locations) {
  //     updatedFormData.locations = [];
  //   }

  //   console.log({ formData }, updatedFormData.locations.length);

  //   updatedFormData.locations.push({
  //     address1: "",
  //     address2: "",
  //     country: "",
  //     state: "",
  //     city: "",
  //     zipCode: "",
  //   });

  //   handleChange("locations", updatedFormData.locations);
  // };

  // useCallback
  // const addMoreLocations = useCallback(() => {
  //   // const updatedLocations = [...locations, locations.length];
  //   // setLocations(updatedLocations);

  //   // const updatedFormData = { ...formData };
  //   const updatedFormData = {
  //     ...formData,
  //     locations: formData.locations ? [...formData.locations.map(loc => ({ ...loc }))] : [],
  //   };
  //   if (!updatedFormData.locations) {
  //     updatedFormData.locations = [];
  //   }

  //   updatedFormData.locations.push({
  //     address1: "",
  //     address2: "",
  //     country: "",
  //     state: "",
  //     city: "",
  //     zipCode: "",
  //   });

  //   handleChange("locations", updatedFormData.locations);
  // }, []);

  //working
  // const addMoreLocations = useCallback(() => {
  //   const newLocations = [...formData.locations]
  //     ? [...formData.locations.map((loc) => ({ ...loc }))]
  //     : [];

  //   newLocations.push({
  //     _id: 1,
  //     address1: "",
  //     address2: "",
  //     country: "",
  //     state: "",
  //     city: "",
  //     zipCode: "",
  //     isDefaultInvoice: false,
  //   });

  //   handleChange("locations", newLocations);
  //   // }, [formData.locations]);
  // }, [formData.locations]);
  const addMoreLocations = () => {
    const maxSize = 5;
    if (formData.locations?.length >= maxSize) {
      showToast("You can add a maximum of 5 locations", "error");
      return;
    }
    const newLocations = [...formData.locations]
      ? [...formData.locations.map((loc) => ({ ...loc }))]
      : [];

    newLocations.push({
      _id: 1,
      address1: "",
      address2: "",
      country: "",
      state: "",
      city: "",
      zipCode: "",
      isDefaultInvoice: false,
    });

    handleChange("locations", newLocations);
  };

  const removeLocation = (index) => {
    const updatedLocations = formData.locations.filter((_, i) => i !== index);
    const removedId = formData.locations[index]?._id;

    if (removedId) {
      setRemovedLocationIds((prev) => [...prev, removedId]);
    }

    handleChange("locations", updatedLocations);
  };

  const handleLocationChange = (index, field, value) => {
    const updatedLocations = [...formData.locations];
    updatedLocations[index] = {
      ...updatedLocations[index],
      [field]: value,
    };

    handleChange("locations", updatedLocations);
  };
  const handleBillingAddressChange = (index) => {
    // setFormData((prevData) => {
    //   const updatedLocations = prevData.locations.map((loc, i) => ({
    //     ...loc,
    //     isDefaultInvoice: i === index,
    //   }));
    //   return {
    //     ...prevData,
    //     locations: updatedLocations,
    //   };
    // });
    setFormData((prevData) => {
      const isCurrentlyDefault = prevData.locations[index].isDefaultInvoice;
      const updatedLocations = prevData.locations.map((loc, i) => ({
        ...loc,
        isDefaultInvoice: isCurrentlyDefault ? false : i === index,
      }));

      return {
        ...prevData,
        locations: updatedLocations,
      };
    });
  };
  //handles changes from store - prefill
  useEffect(() => {
    // if (clientProfileUiid) {
    // if (clientProfileUiid || Object.keys(clientDetails)) {
    if (clientProfileUiid || Object.keys(clientDetails).length > 0) {
      handleChange(
        "currency",
        clientDetails?.generalInfo?.currencyType || undefined
      );
      handleChange(
        "netTerms",
        clientDetails?.generalInfo?.placementDetails?.recurring?.netTerms ||
          undefined
      );
      handleChange(
        "clientCategory",
        clientDetails?.generalInfo?.placementDetails?.recurring?.category ||
          undefined
      );
      handleChange(
        "jobTerminationNotice",
        clientDetails?.generalInfo?.placementDetails?.recurring
          ?.jobTerminationNotice || undefined
      );
      handleChange(
        "placementNetTerms",
        clientDetails?.generalInfo?.placementDetails?.permanent?.netTerms ||
          undefined
      );
      handleChange(
        "commission",
        clientDetails?.generalInfo?.placementDetails?.permanent?.commission ||
          undefined
      );
      handleChange(
        "commissionType",
        clientDetails?.generalInfo?.placementDetails?.permanent
          ?.commissionType || undefined
      );

      const defaultBilling = clientDetails?.generalInfo?.locations?.find(
        (loc) => loc?.isDefaultInvoice
      );

      if (defaultBilling) {
        handleChange("_id", defaultBilling._id || undefined);
        handleChange("address1", defaultBilling.address1 || undefined);
        handleChange("address2", defaultBilling.address2 || undefined);
        handleChange("country", defaultBilling.country || undefined);
        handleChange("state", defaultBilling.state || undefined);
        handleChange("city", defaultBilling.city || undefined);
        handleChange("zipCode", defaultBilling.zipCode || undefined);
      }
      handleChange(
        "locations",
        clientDetails?.generalInfo?.locations || [
          {
            _id:1,
            address1: "",
            address2: "",
            country: "",
            state: "",
            city: "",
            zipCode: "",
            isDefaultInvoice: false,
          },
        ]
      );
      dispatch(
          addToDraft({ prefilled:true })
        );
      // showToast("Client details prefilled", "success");
    }
    const isEmptyObject = (obj) =>
      !obj || Object.values(obj).every((val) => val == null || val === "");

    const placementDetails = clientDetails?.generalInfo?.placementDetails;

    setPlacementType(
      !isEmptyObject(placementDetails?.permanent)
        ? 2
        : !isEmptyObject(placementDetails?.recurring)
        ? 1
        : 1 // default to 1 if both are empty or undefined
    );
    // }, [clientDetails]);
    }, [Object.keys(clientDetails).length,clientProfileUiid]);
  // }, [clientProfileUiid]);

  const handleDraft = useMemo(
    () =>
      debounce((value) => {
        dispatch(
          // addToDraft({
          //   [field]: value ? value : "",
          // })
          addToDraft({ ...value })
        );
      }, 1000),
    [dispatch]
  );
  useEffect(() => {
    console.log("formData", formData);
    let clientData = {
      generalInfo: {
        currencyType: formData.currency,
        placementDetails: {
          recurring: {
            netTerms: formData.netTerms?.value ?? formData.netTerms,
            category: formData.clientCategory?.value ?? formData.clientCategory,
            jobTerminationNotice:
              formData.jobTerminationNotice?.value ??
              formData.jobTerminationNotice,
          },
          permanent: {
            netTerms: formData.placementNetTerms,
            commission: formData.commission,
            commissionType: formData.commissionType,
          },
        },
        locations: formData.locations?.map((loc) => ({
          _id: loc?._id,
          address1: loc.address1,
          address2: loc.address2,
          country: loc.country,
          state: loc.state,
          city: loc.city,
          zipCode: loc.zipCode,
          isDefaultInvoice: loc.isDefaultInvoice,
        })),
      },
    };
    // if (clientDetails.prefilled) {
    //temporarily turned of the draft 
    if (false) {
      if (clientProfileUiid || Object.keys(clientDetails).length > 0) {
        console.log("saga store",clientDetails);
        handleDraft(clientData);
      }
    }
  }, [formData]);

  return (
    <div className="flex flex-col w-full h-full px-4 py-3 overflow-auto rounded md:px-8">
            <DevNote>
              Client ID{clientId}clientProfileUiid:{clientProfileUiid}
            </DevNote>
      <div className="mb-8 md:mb-8">
        <PageHeader
          Section={
            <div className="flex">
              <PageTitle
                title={"General Information"}
                icon={"badge"}
                tag="h5"
              />
            </div>
          }
          RightSection={
            <>
              {/* <Button
                title="Back"
                size="sm"
                icon={"arrow_left_alt"}
                onClick={handleBack}
              />
              <Button title="Save" size="sm" onClick={handleSave} /> */}

              {/* {!isEditable ? (
                <>
                  <Button
                    title="Back"
                    size="sm"
                    icon={"arrow_left_alt"}
                    onClick={handleBack}
                  />
                  <Button title="Save" size="sm" onClick={handleSave} />
                </>
              ) : (
                null
              )} */}
              {isEditable && editClient && (
                <>
                  {editClient !== "/client/client-details" && (
                    <Button
                      title="Back"
                      size="sm"
                      icon={"arrow_left_alt"}
                      onClick={handleBack}
                    />
                  )}

                  <Button title="Save" size="sm" onClick={handleSave} />
                </>
              )}
            </>
          }
        />
      </div>
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-x-8 md:gap-x-6 lg:gap-x-8 gap-y-4 max-w-7xl ">
        <div className="col-span-1">
          <div className="mb-3">
            <PageHeader
              Section={
                <div className="flex">
                  <PageTitle title={"Currency Details"} tag="h6" />
                </div>
              }
            />
          </div>
          <FormFieldSet
            type="select"
            title="Currency"
            placeholder="Select Currency"
            options={CurrencyType}
            value={formData.currency}
            onChange={(value) => handleChange("currency", value)}
            error={errors.currency}
            required
            disabled={!isEditable}
          />
        </div>

        <div className="col-span-1 md:col-span-2 lg:col-span-3">
          <FormFieldSet
            type="radio"
            title={""}
            options={[
              {
                label: "Recurring Placement Details",
                value: 1,
              },
              {
                label: "Permanent Placement",
                value: 2,
              },
            ]}
            value={placementType}
            // onChange={(event) => setPlacementType(Number(event.target.value))}
            onChange={(event) =>
              handlePlacementType(Number(event.target.value))
            }
          />
        </div>

        {placementType === 1 ? (
          <div className="col-span-1 mt-1 md:col-span-2 lg:col-span-3 ">
            <div className="mb-3">
              <PageHeader
                Section={
                  <div className="flex">
                    <PageTitle title={"Recurring Placement Details"} tag="h6" />
                  </div>
                }
              />
            </div>

            <div className="grid grid-cols-1 gap-3 md:grid-cols-2 lg:grid-cols-3 md:gap-6">
              <div>
                <FormFieldSet
                  // type="netTerm"
                  type="select"
                  title="Net Terms"
                  placeholder="Select Net Terms"
                  options={netTermsOptions}
                  value={formData.netTerms}
                  onChange={(value) => handleChange("netTerms", value)}
                  error={errors.netTerms}
                  showSearch
                  // showButton
                  disabled={!isEditable}
                />
                {/* <FormFieldSet
                  type="select"
                  title="Net Terms"
                  placeholder="Select Net Terms"
                  options={netTermsOptions}
                  value={formData.netTerms}
                  onChange={(value) => handleChange("netTerms", value)}
                  error={errors.netTerms}
                  showSearch
                  showButton
                  disabled={!isEditable}
                /> */}
              </div>

              <div>
                <FormFieldSet
                  type="clientCategory"
                  title="Client Category"
                  placeholder="Select Client Category"
                  options={clientCategoryOptions}
                  value={formData.clientCategory}
                  onChange={(value) => handleChange("clientCategory", value)}
                  error={errors.clientCategory}
                  showButton
                  showSearch
                  disabled={!isEditable}
                />
                {/* <FormFieldSet
                  type="select"
                  title="Client Category"
                  placeholder="Select Client Category"
                  options={clientCategoryOptions}
                  value={formData.clientCategory}
                  onChange={(value) => handleChange("clientCategory", value)}
                  error={errors.clientCategory}
                  showButton
                  showSearch
                  disabled={!isEditable}
                /> */}
              </div>

              <div>
                <FormFieldSet
                  type="jobTerminationNotice"
                  title="Job Termination Notice"
                  placeholder="Select Job Termination Notice"
                  options={jobTerminationNoticeOptions}
                  value={formData.jobTerminationNotice}
                  onChange={(value) =>
                    handleChange("jobTerminationNotice", value)
                  }
                  error={errors.jobTerminationNotice}
                  showButton
                  showSearch
                  disabled={!isEditable}
                />
                {/* <FormFieldSet
                  type="select"
                  title="Job Termination Notice"
                  placeholder="Select Job Termination Notice"
                  options={jobTerminationNoticeOptions}
                  value={formData.jobTerminationNotice}
                  onChange={(value) =>
                    handleChange("jobTerminationNotice", value)
                  }
                  error={errors.jobTerminationNotice}
                  showButton
                  showSearch
                  disabled={!isEditable}
                /> */}
              </div>
            </div>
          </div>
        ) : (
          <div className="col-span-1 mt-1 md:col-span-2 lg:col-span-3">
            <div className="mb-3">
              <PageHeader
                Section={
                  <div className="flex">
                    <PageTitle title={"Permanent Placement"} tag="h6" />
                  </div>
                }
              />
            </div>
            <div className="grid grid-cols-1 gap-3 md:grid-cols-2 lg:grid-cols-3 md:gap-6">
              <div>
                <FormFieldSet
                  type="select"
                  title="Net Terms"
                  options={netTermsOptions}
                  placeholder="Select Net Terms"
                  value={formData.placementNetTerms}
                  onChange={(value) => handleChange("placementNetTerms", value)}
                  error={errors.placementNetTerms}
                  // showButton
                  showSearch
                  disabled={!isEditable}
                />
              </div>
              <div>
                <FormFieldSet
                  type="select"
                  title="Commision Type"
                  placeholder="Enter Commision Type"
                  options={CommissionType}
                  value={formData.commissionType}
                  onChange={(value) => handleChange("commissionType", value)}
                  error={errors.commissionType}
                  disabled={!isEditable}
                />
              </div>
              {/* <div className="cols-span-1">
                  <div>
                <FormFieldSet
                  type="text"
                  title="Commision"
                  placeholder="Enter Commision"
                  value={formData.commission}
                  onChange={(value) => handleChange("commission", value)}
                  error={errors.commission}
                  disabled={!isEditable}
                  />
                  </div>
                  {formData.commissionType === 2 && (
                    <div
                    className="bg-primary text-white p-[7px] mt-[18px] rounded-tr-sm rounded-br-sm cursor-pointer"
                  >
                    %
                  </div>
                  )}
              </div> */}
              <div className="cols-span-1 flex items-start">
                <div className="flex-grow">
                  <FormFieldSet
                    type="text"
                    title="Commision"
                    placeholder="Enter Commision"
                    value={formData.commission}
                    onChange={(value) => handleChange("commission", value)}
                    error={errors.commission}
                    disabled={!isEditable}
                  />
                </div>
                {formData.commissionType === 2 && (
                  <div className="bg-primary text-white p-[7px] mt-[20px] rounded-tr-sm rounded-br-sm cursor-pointer">
                    %
                  </div>
                )}
              </div>

              {/* <div>
                <FormFieldSet
                  type="select"
                  title="Commision Type"
                  placeholder="Enter Commision Type"
                  options={CommissionType}
                  value={formData.commissionType}
                  onChange={(value) => handleChange("commissionType", value)}
                  error={errors.commissionType}
                  disabled={!isEditable}
                />
              </div> */}
            </div>
          </div>
        )}

        {/* <div className="col-span-1 mt-1 md:col-span-2 lg:col-span-3 ">
          <div className="mb-3">
            <PageHeader
              Section={
                <div className="flex">
                  <PageTitle title={"Recurring Placement Details"} tag="h6" />
                </div>
              }
            />
          </div>

          <div className="grid grid-cols-1 gap-3 md:grid-cols-2 lg:grid-cols-3 md:gap-6">
            <div>
              <FormFieldSet
                type="select"
                title="Net Terms"
                placeholder="Enter Net Terms"
                options={netTermsOptions}
                value={formData.netTerms}
                onChange={(value) => handleChange("netTerms", value)}
                error={errors.netTerms}
                showSearch
                showButton
                disabled={!isEditable}
              />
            </div>

            <div>
              <FormFieldSet
                type="select"
                title="Client Category"
                placeholder="Enter Client Category"
                options={clientCategoryOptions}
                value={formData.clientCategory}
                onChange={(value) => handleChange("clientCategory", value)}
                error={errors.clientCategory}
                showButton
                showSearch
                disabled={!isEditable}
              />
            </div>

            <div>
              <FormFieldSet
                type="select"
                title="Job Termination Notice"
                placeholder="Enter Job Termination Notice"
                options={jobTerminationNoticeOptions}
                value={formData.jobTerminationNotice}
                onChange={(value) =>
                  handleChange("jobTerminationNotice", value)
                }
                error={errors.jobTerminationNotice}
                showButton
                showSearch
                disabled={!isEditable}
              />
            </div>
          </div>
        </div>

        <div className="col-span-1 mt-1 md:col-span-2 lg:col-span-3">
          <div className="mb-3">
            <PageHeader
              Section={
                <div className="flex">
                  <PageTitle title={"Permanent Placement"} tag="h6" />
                </div>
              }
            />
          </div>
          <div className="grid grid-cols-1 gap-3 md:grid-cols-2 lg:grid-cols-3 md:gap-6">
            <div>
              <FormFieldSet
                type="select"
                title="Net Terms"
                options={jobTerminationNoticeOptions}
                placeholder="Select Net Terms"
                value={formData.placementNetTerms}
                onChange={(value) => handleChange("placementNetTerms", value)}
                error={errors.placementNetTerms}
                showButton
                showSearch
                disabled={!isEditable}
              />
            </div>
            <div>
              <FormFieldSet
                type="text"
                title="Commision"
                placeholder="Enter Commision"
                value={formData.commission}
                onChange={(value) => handleChange("commission", value)}
                error={errors.commission}
                disabled={!isEditable}
              />
            </div>
            <div>
              <FormFieldSet
                type="select"
                title="Commision Type"
                placeholder="Enter Commision Type"
                options={CommissionType}
                value={formData.commissionType}
                onChange={(value) => handleChange("commissionType", value)}
                error={errors.commissionType}
                disabled={!isEditable}
              />
            </div>
          </div>
        </div> */}
      </div>

      <div className="mt-6">
        <div className="flex justify-between">
          <div className="mb-3">
            <PageHeader
              Section={<PageTitle title={"Client Location"} tag="h6" />}
            />
          </div>
          <div>
            <Button
              variant="text"
              type={"link"}
              size={"sm"}
              title="add more"
              icon={"add"}
              color={"info"}
              onClick={addMoreLocations}
              disabled={!isEditable}
            />
          </div>
        </div>
        {/* {formData?.locations.map((_, index) => ( */}
        {/* {locations.map((index) => ( */}
        {[
          ...(formData?.locations?.length
            ? formData.locations
            : [
                {
                  address1: "",
                  address2: "",
                  country: "",
                  state: "",
                  city: "",
                  zipCode: "",
                  isDefaultInvoice: false,
                },
              ]),
        ].map((_, index) => (
          <Locations
            index={index}
            formData={formData}
            errors={errors}
            isEditable={isEditable}
            location={_}
            hasDefaultACheck
            handleBillingAddressChange={handleBillingAddressChange}
            handleLocationChange={handleLocationChange}
            showDeleteButton={true}
            removeLocation={removeLocation}
          />
        ))}
        {/* <ul className="list-disc pl-6">
          {formData?.locations?.map((location) => (
            <li key={location?._id || 1}>{location?._id || 1}</li>
          ))}
        </ul> */}

        {/* {[...formData?.locations].map((_, index) => (
          <Locations
            index={index}
            formData={formData}
            errors={errors}
            isEditable={isEditable}
            location={_}
            hasDefaultCheck
            handleLocationChange={handleLocationChange}
          />
        ))} */}
      </div>
      <div className="col-span-1 mt-1 md:col-span-2 lg:col-span-3">
        <div className="mb-3">
          <PageHeader
            Section={
              <div className="flex">
                <PageTitle title={"Invoice Location"} tag="h6" />
              </div>
            }
            // RightSection={
            //   <FormFieldSet
            //     type="checkbox"
            //     size={"md"}
            //     title={""}
            //     options={[
            //       {
            //         label: "mark Client Location as Invoice Location",
            //         value: true,
            //       },
            //     ]}
            //     values={formData.markClientLocation || []}
            //     onChange={(value) => handleChange("markClientLocation", value)}
            //     multiple
            //     error={errors.markClientLocation}
            //   />
            // }
          />
        </div>
        {/* //invoice locations */}
        <div className="grid grid-cols-1 gap-3 md:grid-cols-2 lg:grid-cols-3 md:gap-6 max-w-7xl">
          <div>
            <FormFieldSet
              type="text"
              title="Address Line 1"
              placeholder="Enter Address"
              // value={() => {
              //   const index = formData?.locations?.findIndex(
              //     (loc) => loc?.isDefaultInvoice === true
              //   );
              //   return index !== -1 ? formData.locations[index]?.address1 : "";
              // }}
              // value={formData.address1}
              value={
                formData.locations.find((l) => l?.isDefaultInvoice)?.address1 ||
                ""
              }
              onChange={(value) => handleChange("address1", value)}
              error={errors.address1}
              disabled={!isEditable}
            />
          </div>
          <div>
            <FormFieldSet
              type="text"
              title="Address Line 2"
              placeholder="Enter Address"
              // value={formData.address2}
              value={
                formData.locations.find((l) => l?.isDefaultInvoice)?.address2 ||
                ""
              }
              onChange={(value) => handleChange("address2", value)}
              error={errors.address2}
              disabled={!isEditable}
            />
          </div>
          <div className="lg:col-span-1 md:col-span-1">
            <FormFieldSet
              type="select"
              title="Country"
              placeholder="Select Country"
              options={countryOptions}
              // value={formData.country}
              value={
                formData.locations.find((l) => l?.isDefaultInvoice)?.country ||
                ""
              }
              onChange={(value) => handleChange("country", value)}
              error={errors.country}
              disabled={!isEditable}
            />
          </div>
          <div className="col-span-1 md:col-span-1 lg:col-span-1">
            <FormFieldSet
              type="select"
              title="State"
              keyname="states"
              placeholder="Select State"
              options={stateOptions}
              searchValue={stateSearch}
              onSearch={handleStateSearch}
              onClose={resetStateSearch}
              showSearch
              // value={formData.state}
              value={
                formData.locations.find((l) => l?.isDefaultInvoice)?.state || ""
              }
              onChange={(value) => handleChange("state", value)}
              error={errors.state}
              disabled={!isEditable || !formData.country}
              refData={containerRef}
              handleScroll={() => handleStateScroll(containerRef)}
            />
          </div>
          <div className="col-span-1 md:col-span-1 lg:col-span-1">
            <FormFieldSet
              type="select"
              title="City"
              keyname="cities"
              placeholder="Select City"
              options={cityOptions}
              searchValue={citySearch}
              onSearch={handleCitySearch}
              onClose={resetCitySearch}
              showSearch
              // value={formData.city}
              value={
                formData.locations.find((l) => l?.isDefaultInvoice)?.city || ""
              }
              onChange={(value) => handleChange("city", value)}
              error={errors.city}
              disabled={!isEditable || !formData.country || !formData.state}
              refData={containerRef}
              handleScroll={() => handleCityScroll(containerRef)}
            />
          </div>
          <div className="col-span-1 md:col-span-1 lg:col-span-1">
            <FormFieldSet
              type="text"
              title="Zip Code"
              placeholder="Enter Zip Code"
              // value={formData.zipCode}
              value={
                formData.locations.find((l) => l?.isDefaultInvoice)?.zipCode ||
                ""
              }
              onChange={(value) => handleChange("zipCode", value)}
              error={errors.zipCode}
              disabled={!isEditable}
            />
          </div>
        </div>
      </div>
    </div>
  );
};

export default GeneralInformation;
